import type { OnTrack } from '../index.js'
import type { WorkerTier } from '../../core/index.js'
import { TIER_HEX, TIER_LABELS } from '../../core/index.js'
import { ScoreRing } from './ScoreRing.js'
import { Star, TrendingDown, TrendingUp } from 'lucide-react'

/** The last day of the period, from its exclusive end date: "Oct 31" for 2026-11-01. */
export function lastDayOf(periodEnd: string): string {
  const lastDay = new Date(Date.parse(`${periodEnd}T00:00:00Z`) - 86_400_000)
  return lastDay.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export const TIER_RANK: Record<WorkerTier, number> = { gold: 3, silver: 2, bronze: 1, probationary: 0 }

/** The tier held: solid, and the only badge when the running score points nowhere else. */
function HeldBadge({ tier, text, quiet = false }: { tier: WorkerTier; text: string; quiet?: boolean }) {
  return (
    <div
      className={`flex w-full items-center justify-center gap-1 rounded-full font-extrabold mt-1.5 ${quiet ? 'px-2 py-0.5 text-[9.5px]' : 'px-2.5 py-1 text-[10px]'}`}
      style={{ background: quiet ? 'transparent' : `${TIER_HEX[tier]}33`, color: TIER_HEX[tier], border: `1.5px solid ${TIER_HEX[tier]}` }}
    >
      <Star aria-hidden="true" fill="currentColor" className="h-3 w-3" />
      {text}
    </div>
  )
}

/**
 * Where the running score is heading, as the headline under the dial: the
 * tier named, larger than the held tier beneath it, with an arrow for the
 * direction. Dashed, since it is not held yet - only a close sets a tier.
 */
function OnTrackBadge({ tier, held }: { tier: WorkerTier; held: WorkerTier }) {
  const Arrow = TIER_RANK[tier] >= TIER_RANK[held] ? TrendingUp : TrendingDown
  return (
    <div className="mt-2 flex w-full flex-col items-center rounded-lg px-2 py-1.5" style={{ background: `${TIER_HEX[tier]}22`, color: TIER_HEX[tier], border: `2px dashed ${TIER_HEX[tier]}` }}>
      <span className="text-[8.5px] font-bold uppercase tracking-wide">On track for</span>
      <span className="flex items-center gap-1 text-sm font-black leading-tight">
        <Arrow aria-hidden="true" className="h-4 w-4" />
        {TIER_LABELS[tier]}
      </span>
    </div>
  )
}

/** Why the tier and the score do not line up, in one sentence. Null when there is nothing to explain. */
export function onTrackExplanation(tier: WorkerTier, onTrack: OnTrack): string | null {
  const closes = `the period closes after ${lastDayOf(onTrack.periodEnd)}`
  if (onTrack.tier !== tier) {
    const provisional = onTrack.provisional ? ' Provisional until more shifts are worked.' : ''
    return `${TIER_LABELS[tier]} until ${closes}. A close on this score would set ${TIER_LABELS[onTrack.tier]}.${provisional}`
  }
  if (onTrack.reason === 'new_hire') {
    return `Stays ${TIER_LABELS[tier]} when ${closes}, whatever the score: still within the new-hire period.`
  }
  if (onTrack.reason === 'provisional_held') {
    return `Keeps ${TIER_LABELS[tier]} when ${closes}: a provisional score does not move a worker down.`
  }
  return null
}

/**
 * A worker's score dial with the tier they hold. When the running score
 * points somewhere else, the tier they are on track for leads: the dial
 * takes its colour and a dashed headline names it, with the held tier
 * beneath as "Bronze now". A tier is only set when a period closes, so mid-period a score
 * of 61 can sit beside Bronze; this names both and says why, rather than
 * leaving the pair looking like a mistake. `onTrack` comes from the API, which applies
 * the close rules; nothing here works a tier out from a score.
 */
export function ScoreStanding({ score, tier, onTrack, tierSuffix = '' }: { score: number; tier: WorkerTier; onTrack: OnTrack | null | undefined; tierSuffix?: string }) {
  // Also absent, not just null, from an API deployed before the field existed.
  const heading = onTrack && onTrack.tier !== tier ? onTrack : null
  const note = onTrack && onTrackExplanation(tier, onTrack)

  return (
    // Held to little more than the dial's own width, with the pills stacked: it sits in an auto-sized
    // column beside the worker's detail, and anything wider squeezes the columns next to it.
    <div className="text-center w-36 mx-auto">
      {/* The dial takes the colour of where the score is heading, so a 94 never sits in a Bronze ring. */}
      <ScoreRing score={score} tier={heading ? heading.tier : tier} />
      <div className="flex flex-col">
        {heading && <OnTrackBadge tier={heading.tier} held={tier} />}
        <HeldBadge tier={tier} text={heading ? `${TIER_LABELS[tier]}${tierSuffix} now` : `${TIER_LABELS[tier]}${tierSuffix}`} quiet={Boolean(heading)} />
      </div>
      {note && <div className="text-[10px] text-ink-mid leading-snug mt-2">{note}</div>}
    </div>
  )
}
